import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, App, Button, Skeleton, Space } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { extractErrorMessage } from '../../../lib/utils/checkErrorResponse';
// 결제 설정과 같은 '결제 관리' 메뉴라 편집·저장 UI 를 그대로 공유한다.
import { useSettingsDraft } from '../../pgProviderRoute/hooks/useSettingsDraft';
import { SettingsSection } from '../../pgProviderRoute/common/SettingsSection';
import { SettingsSaveBar } from '../../pgProviderRoute/common/SettingsSaveBar';
import { pgProviderFeeQuery } from '../queries';
import { useUpdatePgProviderFeesMutation } from '../hooks/usePgProviderFeeMutations';
import { getChangedFees, getFeeChanges, getReverseMarginFees, toComparableFees } from '../utils/feeChanges';
import { EditableFeeField } from '../types';
import { FeeTable } from './FeeTable/FeeTable';
import { FeeReviewModal, REASON_MIN_LENGTH } from './FeeReviewModal';

type FeePanelProps = { canUpdate: boolean; onDirtyChange: (isDirty: boolean) => void };

export const FeePanel = (props: FeePanelProps): React.JSX.Element => {
  const { canUpdate, onDirtyChange } = props;
  const query = useQuery(pgProviderFeeQuery.list());
  // 마지막 결제일·휴면 여부는 서버가 매 응답 새로 계산한다. 비교에 넣으면 결제가 한 건만
  // 더 들어와도 "다른 변경 사항이 조회되었습니다" 로 저장이 막힌다 — 편집 대상만 비교한다.
  const draft = useSettingsDraft(query.data?.fees, toComparableFees);
  const mutation = useUpdatePgProviderFeesMutation();
  const { message } = App.useApp();
  const [isReviewOpen, setReviewOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  useEffect(() => onDirtyChange(draft.isDirty), [draft.isDirty, onDirtyChange]);

  if (query.isError)
    return (
      <Alert
        type="error"
        showIcon
        message="PG 수수료를 불러오지 못했습니다."
        action={<Button onClick={() => query.refetch()}>다시 시도</Button>}
      />
    );
  if (!draft.value || !draft.baseline) return <Skeleton active paragraph={{ rows: 7 }} />;

  const fees = draft.value;
  const changes = getFeeChanges(draft.baseline, fees);
  const changedFees = getChangedFees(draft.baseline, fees);
  // 비운 요율은 0 이 아니라 "미입력" 이다. 요율은 필수라 저장을 막는다.
  const isInvalid = fees.some((fee) => fee.feeRate === null || fee.feeRateActual === null);

  const handleChange = (id: number, field: EditableFeeField, value: number | null): void =>
    draft.update((current) => current.map((fee) => (fee.id === id ? { ...fee, [field]: value } : fee)));

  /**
   * 🔴 저장 직전에 한 번 더 읽어 다른 사람의 변경을 확인한다.
   *
   * 이 화면은 열어 둔 뒤 자동으로 다시 조회하는 경로가 없다 — 전역 `refetchOnWindowFocus` 가
   * 꺼져 있고 탭은 `forceRender` 라 재마운트되지 않는다. 그래서 확인 없이 저장하면
   * 그 사이 다른 관리자가 바꾼 내용을 모른 채 덮어쓴다(서버 잠금은 동시에 들어온 저장을
   * 줄 세울 뿐, 오래된 기준값으로 온 요청은 막지 못한다).
   */
  const handleReview = async (): Promise<void> => {
    setSaveError(null);
    const { data } = await query.refetch();

    if (draft.isOutdated(data?.fees)) {
      message.warning('다른 관리자가 설정을 바꿨습니다. 변경 취소 후 최신 설정으로 다시 편집해 주세요.');

      return;
    }
    setReviewOpen(true);
  };
  const handleSave = async (): Promise<void> => {
    if (!canUpdate || mutation.isLoading || draft.hasRemoteChanges || isInvalid) return;
    if (reason.trim().length < REASON_MIN_LENGTH) return;
    setSaveError(null);
    try {
      await mutation.mutateAsync({ fees: changedFees, reason: reason.trim() });
      draft.reset();
      setReviewOpen(false);
      setReason('');
      message.success('PG 수수료를 저장했습니다.');
    } catch (error) {
      setSaveError(error instanceof Error ? extractErrorMessage(error) : '저장하지 못했습니다. 다시 시도해 주세요.');
    }
  };

  return (
    <>
      {draft.hasRemoteChanges && (
        <Alert
          className="settings-inline-alert"
          type="warning"
          showIcon
          message="다른 변경 사항이 조회되었습니다. 변경 취소 후 최신 수수료로 다시 편집해 주세요."
        />
      )}
      <SettingsSection
        title="PG 사별 수수료"
        description="(PG 사 × 결제수단) 조합은 고정입니다. 값만 수정할 수 있고, 새 PG 개통은 개발 배포가 필요합니다."
        action={
          <Space size={12}>
            <span className="settings-muted">{fees.length}개 조합</span>
            <Button icon={<ReloadOutlined />} loading={query.isFetching} onClick={() => query.refetch()}>
              새로고침
            </Button>
          </Space>
        }
      >
        <FeeTable fees={fees} canUpdate={canUpdate} isLoading={query.isLoading} onChange={handleChange} />
      </SettingsSection>
      <SettingsSaveBar
        isDirty={draft.isDirty}
        isSaving={mutation.isLoading}
        canUpdate={canUpdate}
        disabled={draft.hasRemoteChanges || isInvalid}
        onReset={() => {
          draft.reset();
          setReason('');
        }}
        onReview={handleReview}
      />
      <FeeReviewModal
        open={isReviewOpen}
        changes={changes}
        reverseMarginFees={getReverseMarginFees(fees)}
        reason={reason}
        isSaving={mutation.isLoading}
        error={saveError}
        onReasonChange={setReason}
        onSave={handleSave}
        onClose={() => setReviewOpen(false)}
      />
    </>
  );
};
